/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import Input from '../../components/UI/Input/Input';
import Modal from '../../components/UI/Modal/Modal';
import TextArea from '../../components/UI/TextArea/TextArea';
import {
  SelfServedClient,
  useCreateSelfServedClient,
  useUpdateSelfServedClient,
} from '../../services/queries/useSelfServedClients';

interface SelfServedClientFormProps {
  open: boolean;
  onClose: () => void;
  client: SelfServedClient | null;
}

const SelfServedClientForm = ({ open, onClose, client }: SelfServedClientFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useCreateSelfServedClient();
  const updateMutation = useUpdateSelfServedClient();

  const isEdit = !!client;
  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        description: client.description,
        isActive: client.isActive,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        isActive: true,
      });
    }
    setErrors({});
  }, [client, open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEdit && client) {
        await updateMutation.mutateAsync({
          id: client.id,
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive,
        });
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          description: formData.description,
        });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save client:', error);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal
      isOpen={open}
      setIsOpen={onClose}
      type="large"
      height="auto"
      showCloseIcon
      title={isEdit ? 'Edit Client' : 'Create New Client'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Client Name *
          </label>
          <Input
            label=""
            value={formData.name}
            width="w-full"
            onChange={e => handleInputChange('name', e.target.value)}
            placeholder="Enter client name"
            classNameInput={errors.name ? 'border-red-500' : ''}
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Description *
          </label>
          <TextArea
            name="description"
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            placeholder="Enter client description"
            error={!!errors.description}
            disabled={isLoading}
            width="w-full"
          />
        </div>

        {isEdit && (
          <div className="flex items-center justify-between">
            <div>
              <label
                htmlFor="isActive"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Status
              </label>
              <p className="text-sm text-gray-500">Enable or disable this client</p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={e => handleInputChange('isActive', e.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : isEdit ? 'Update Client' : 'Create Client'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SelfServedClientForm;
